export const getSelectHelpers = ({ error, darkMode, controlPadding }) =>
{
    const borderClass = error ? '#e53e3e' : '#e2e8f0';
    const borderFocusClass = error ? '#e53e3e' : 'rgb(190 159 128)';
    const optionBackground = darkMode ? '#6B6F76' : '#eeeeee';
    return {
        container: (base) => ({
            ...base,
            width: '100%',
        }),
        control: (base) => ({
            ...base,
            background: darkMode ? '#525252' : '#ffffff',
            color: darkMode ? '#eeeeee' : '#333333',
            boxShadow: 'none !important',
            lineHeight: '1.25 !important',
            minHeight: '26px !important',
            border: `1px solid ${darkMode ? '#9CA3AF' : borderClass} !important`,
            borderRadius: '4px',
            padding: controlPadding ?? '8px 6px',
            ':focus-within': {
                background: darkMode ? '#525252' : '#ffffff !important',
                border: `1px solid ${darkMode ? '#ffffff' : borderFocusClass} !important`,
            },
        }),
        dropdownIndicator: (base) => ({
            ...base,
            padding: 4,
        }),
        clearIndicator: (base) => ({
            ...base,
            padding: 4,
        }),
        valueContainer: (base) => ({
            ...base,
            color: darkMode ? '#eeeeee' : '#333333',
            padding: '0px 6px',
        }),
        input: (base) => ({
            ...base,
            color: darkMode ? '#eeeeee' : '#333333',
            margin: 0,
            padding: 0,
        }),
        menu: (base) => ({
            ...base,
            background: darkMode ? '#525252' : '#ffffff',
        }),
        option: (base, {
            isFocused,
        }) => ({
            ...base,
            color: darkMode ? '#eeeeee' : '#333333',
            backgroundColor: isFocused ? `${optionBackground} !important` : 'transparent !important',
            ':active': {
                ...base[':active'],
                backgroundColor: darkMode ? '#4B515C !important' : '#B2D4FF !important',
            },
        }),
        singleValue: (base) => ({
            ...base,
            color: darkMode ? '#eeeeee' : '#333333',
        }),
        menuPortal: (base) => ({
            ...base,
            zIndex: 10000,
        }),
    };
};

const themeHelpers = {
    bg_colors: {
        default: 'tw-bg-transparent',
        primary: 'tw-bg-black',
        info: 'tw-bg-info-default',
        success: 'tw-bg-success-default',
        warning: 'tw-bg-warning-default',
        danger: 'tw-bg-danger-default',
        disabled: 'tw-bg-gray-200',
        green: 'tw-bg-green-default',
    },
    lighter_bg_colors: {
        default: 'tw-bg-transparent',
        primary: 'tw-bg-primary-lightest',
        info: 'tw-bg-info-lightest',
        success: 'tw-bg-success-lightest',
        warning: 'tw-bg-warning-lightest',
        danger: 'tw-bg-danger-lightest',
        disabled: 'tw-bg-gray-200',
        green: 'tw-bg-green-lightest',
    },
    hover_colors: {
        default: 'hover:tw-bg-transparent',
        primary: 'hover:tw-bg-primary-default',
        info: 'hover:tw-bg-info-default',
        success: 'hover:tw-bg-success-default',
        warning: 'hover:tw-bg-warning-default',
        danger: 'hover:tw-bg-danger-default',
        disabled: 'tw-bg-gray-200',
        green: 'hover:tw-bg-green-default',
    },
    darker_hover_colors: {
        default: 'hover:tw-bg-transparent',
        primary: 'hover:tw-bg-primary-darker',
        info: 'hover:tw-bg-info-darker',
        success: 'hover:tw-bg-success-darker',
        warning: 'hover:tw-bg-warning-darker',
        danger: 'hover:tw-bg-danger-darker',
        disabled: 'tw-bg-gray-200',
        green: 'hover:tw-bg-green-darker',
    },
    focus_colors: {
        default: 'focus:tw-bg-transparent',
        primary: 'focus:tw-bg-primary-default',
        info: 'focus:tw-bg-info-default',
        success: 'focus:tw-bg-success-default',
        warning: 'focus:tw-bg-warning-default',
        danger: 'focus:tw-bg-danger-default',
        disabled: 'tw-bg-gray-200',
        green: 'focus:tw-bg-green-default',
    },
    darker_focus_colors: {
        default: 'focus:tw-bg-transparent',
        primary: 'focus:tw-bg-primary-darker',
        info: 'focus:tw-bg-info-darker',
        success: 'focus:tw-bg-success-darker',
        warning: 'focus:tw-bg-warning-darker',
        danger: 'focus:tw-bg-danger-darker',
        disabled: 'tw-bg-gray-200',
        green: 'focus:tw-bg-green-darker',
    },
    text_colors: {
        default: 'tw-text-default',
        primary: 'tw-text-primary-default',
        info: 'tw-text-info-default',
        success: 'tw-text-success-default',
        warning: 'tw-text-warning-default',
        danger: 'tw-text-danger-default',
        disabled: 'tw-bg-gray-200',
        green: 'tw-text-green-default',
    },
    hover_text_colors: {
        default: 'hover:tw-text-default',
        primary: 'hover:tw-text-primary-default',
        info: 'hover:tw-text-info-default',
        success: 'hover:tw-text-success-default',
        warning: 'hover:tw-text-warning-default',
        danger: 'hover:tw-text-danger-default',
        disabled: 'tw-bg-gray-200',
        green: 'hover:tw-text-green-default',
    },
    focus_text_colors: {
        default: 'focus:tw-text-default',
        primary: 'focus:tw-text-primary-default',
        info: 'focus:tw-text-info-default',
        success: 'focus:tw-text-success-default',
        warning: 'focus:tw-text-warning-default',
        danger: 'focus:tw-text-danger-default',
        disabled: 'tw-bg-gray-200',
        green: 'focus:tw-text-green-default',
    },
    border_colors: {
        default: 'tw-border-default',
        primary: 'tw-border-primary-default',
        info: 'tw-border-info-default',
        success: 'tw-border-success-default',
        warning: 'tw-border-warning-default',
        danger: 'tw-border-danger-default',
        disabled: 'tw-bg-gray-200',
        green: 'tw-border-green-default',
    },
    font_size: {
        xs: 'tw-text-xs',
        sm: 'tw-text-sm',
    },
    font: {
        lato: 'tw-font-lato',
    },
    padding: {
        xs: {
            paddingY: 'tw-py-0',
            paddingX: 'tw-px-2',
        },
        sm: {
            paddingY: 'tw-py-1',
            paddingX: 'tw-px-2',
        },
        md: {
            paddingY: 'tw-py-2',
            paddingX: 'tw-px-4',
        },
        lg: {
            paddingY: 'tw-py-4',
            paddingX: 'tw-px-8',
        },
    },
    margin: {
        xs: {
            marginY: 'tw-my-0',
            marginX: 'tw-mx-2',
        },
        sm: {
            marginY: 'tw-my-1',
            marginX: 'tw-mx-2',
        },
        md: {
            marginY: 'tw-my-2',
            marginX: 'tw-mx-4',
        },
        lg: {
            marginY: 'tw-my-4',
            marginX: 'tw-mx-8',
        },
    },
    column_sizes: {
        xs: {
            1: 'tw-w-1/12',
            2: 'tw-w-2/12',
            3: 'tw-w-3/12',
            4: 'tw-w-4/12',
            5: 'tw-w-5/12',
            6: 'tw-w-6/12',
            7: 'tw-w-7/12',
            8: 'tw-w-8/12',
            9: 'tw-w-9/12',
            10: 'tw-w-10/12',
            11: 'tw-w-11/12',
            12: 'tw-w-12/12',
        },
        sm: {
            1: 'sm:tw-w-1/12',
            2: 'sm:tw-w-2/12',
            3: 'sm:tw-w-3/12',
            4: 'sm:tw-w-4/12',
            5: 'sm:tw-w-5/12',
            6: 'sm:tw-w-6/12',
            7: 'sm:tw-w-7/12',
            8: 'sm:tw-w-8/12',
            9: 'sm:tw-w-9/12',
            10: 'sm:tw-w-10/12',
            11: 'sm:tw-w-11/12',
            12: 'sm:tw-w-12/12',
        },
        md: {
            1: 'md:tw-w-1/12',
            2: 'md:tw-w-2/12',
            3: 'md:tw-w-3/12',
            4: 'md:tw-w-4/12',
            5: 'md:tw-w-5/12',
            6: 'md:tw-w-6/12',
            7: 'md:tw-w-7/12',
            8: 'md:tw-w-8/12',
            9: 'md:tw-w-9/12',
            10: 'md:tw-w-10/12',
            11: 'md:tw-w-11/12',
            12: 'md:tw-w-12/12',
        },
        lg: {
            1: 'lg:tw-w-1/12',
            2: 'lg:tw-w-2/12',
            3: 'lg:tw-w-3/12',
            4: 'lg:tw-w-4/12',
            5: 'lg:tw-w-5/12',
            6: 'lg:tw-w-6/12',
            7: 'lg:tw-w-7/12',
            8: 'lg:tw-w-8/12',
            9: 'lg:tw-w-9/12',
            10: 'lg:tw-w-10/12',
            11: 'lg:tw-w-11/12',
            12: 'lg:tw-w-12/12',
        },
    },
};

export default themeHelpers;
